/**
 * Date helper utilities for voyage management.
 *
 * IMPORTANT — TIMEZONE SAFETY
 * Every YYYY-MM-DD string the app handles represents a **calendar day**,
 * not an instant in time. The user's "Day 1 = 22 May" must stay "22 May"
 * regardless of which timezone they're in.
 *
 * The previous implementation used `new Date("2026-05-22T00:00:00")` (local
 * midnight) and then `.toISOString().slice(0, 10)` to round-trip back to a
 * YYYY-MM-DD string. That round-trip silently shifts the day by 1 for any
 * user east of UTC, because:
 *   - new Date("2026-05-22T00:00:00")    // 22 May 00:00 LOCAL
 *   - .toISOString()                     // 21 May 15:00Z when local = +09:00
 *   - .slice(0, 10)                      // "2026-05-21"  ← BUG
 *
 * This module now uses LOCAL parts (year, month, date) for every
 * round-trip, completely avoiding the UTC conversion path.
 */

/** Parse a YYYY-MM-DD string into a Date pointing at LOCAL midnight.
 *  Returns null on bad input. */
export function parseLocalDate(isoDate) {
  if (!isoDate || typeof isoDate !== "string") return null;
  const m = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const [, y, mo, d] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d));
}

/** Format a Date as YYYY-MM-DD using its LOCAL components.
 *  Never use `.toISOString().slice(0,10)` for this — that's the bug. */
export function formatLocalISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Add N days to a date string (YYYY-MM-DD) → YYYY-MM-DD. Timezone-safe. */
export function addDays(dateStr, n) {
  const d = parseLocalDate(dateStr);
  if (!d) return dateStr;
  d.setDate(d.getDate() + n);
  return formatLocalISO(d);
}

/** Format YYYY-MM-DD → "Mon, 15 Jun 2026" (human-readable, LOCAL). */
export function formatDate(dateStr) {
  const d = parseLocalDate(dateStr);
  if (!d) return "—";
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Format YYYY-MM-DD → "15 Jun" (short, LOCAL). */
export function formatDateShort(dateStr) {
  const d = parseLocalDate(dateStr);
  if (!d) return "—";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

/** Today as YYYY-MM-DD using LOCAL components. */
export function todayISO() {
  return formatLocalISO(new Date());
}

/** Same weekday next month — start 28 days out, then bump forward until the
 * weekday matches. Cruise itineraries repeat weekly, so this picks the same
 * day-of-week roughly a month later (28–34 days out).
 *
 * Returns "" for missing or unparseable input so calling components can
 * pre-fill a date <input> safely.
 */
export function sameWeekdayNextMonth(isoDate) {
  const d = parseLocalDate(isoDate);
  if (!d) return "";
  const targetWeekday = d.getDay();
  const candidate = new Date(d);
  candidate.setDate(candidate.getDate() + 28);
  while (candidate.getDay() !== targetWeekday) {
    candidate.setDate(candidate.getDate() + 1);
  }
  return formatLocalISO(candidate);
}

/** Compute end_date from start_date + total_days */
export function endDate(startDate, totalDays) {
  if (!startDate || !totalDays) return "—";
  return addDays(startDate, totalDays - 1);
}

/** Generate default days array (all Sea) from start_date and total_days.
 *
 *  CRITICAL: `days[0].date === startDate` must hold exactly, in any
 *  timezone. This is verified by `addDays(startDate, 0) === startDate`.
 */
export function generateDefaultDays(startDate, totalDays) {
  return Array.from({ length: totalDays }, (_, i) => ({
    day_number: i + 1,
    date: addDays(startDate, i),
    port_or_sea: "Sea",
    port_name: null,
    arrival_time: null,
    departure_time: null,
    notes: "",
  }));
}
